import './style.css';
import { LoadingComponent } from '../../controls';
import { useMilestonesReducer } from './useMilestonesReducer';
import { Milestone, MilestoneType } from '../../types/milestone';
import { RewardType, RewardTypeLabels } from '../../types/reward';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { RewardEditor } from './RewardEditor';
import React, { useState, useEffect, useMemo } from 'react';
import PackSettingsService from '../../api/packSettingsService';
import { PackSettings } from '../../types/packSettings';

const MilestonesPage = () => {
  const { data, create, update, deleteMilestone, move } = useMilestonesReducer();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [formData, setFormData] = useState<Partial<Milestone>>({
    type: MilestoneType.Primary,
    levelNumber: 1,
    metadata: {
      reward: {
        type: RewardType.Credits,
        amount: 0,
        innerRewards: [],
      },
    },
  });
  const [levelFilter, setLevelFilter] = useState<number | null>(null);
  const [maxLevel, setMaxLevel] = useState<number>(3000);

  useEffect(() => {
    const fetchMaxLevel = async () => {
      try {
        const settings = await PackSettingsService.getAll();
        if (settings.length > 0) {
          const lastSetting = settings[settings.length - 1];
          setMaxLevel(lastSetting.end);
        }
      } catch (error) {
        console.error('Failed to fetch max level:', error);
      }
    };
    fetchMaxLevel();
  }, []);

  const milestonesMap = useMemo(() => {
    const map = new Map<number, Milestone>();
    data.Records.forEach((m) => {
      map.set(m.levelNumber, m);
    });
    return map;
  }, [data.Records]);

  const filteredLevels = useMemo(() => {
    if (levelFilter === null) {
      return Array.from({ length: maxLevel }, (_, i) => i + 1);
    }
    const start = Math.max(1, levelFilter - 10);
    const end = Math.min(maxLevel, levelFilter + 10);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [levelFilter, maxLevel]);

  if (data.Loading && data.Records.length === 0) {
    return <LoadingComponent />;
  }

  const handleCardClick = (levelNumber: number) => {
    const existing = milestonesMap.get(levelNumber);
    if (existing) {
      setEditingMilestone(existing);
      setFormData({
        ...existing,
      });
    } else {
      setEditingMilestone(null);
      setFormData({
        type: MilestoneType.Primary,
        levelNumber,
        metadata: {
          reward: {
            type: RewardType.Credits,
            amount: 0,
            innerRewards: [],
          },
        },
      });
    }
    setDialogVisible(true);
  };

  const handleSave = async () => {
    const milestoneData: Milestone = {
      id: editingMilestone?.id || 0,
      type: formData.type || MilestoneType.Primary,
      levelNumber: formData.levelNumber || 1,
      metadata: formData.metadata || {
        reward: {
          type: RewardType.Credits,
          amount: 0,
          innerRewards: [],
        },
      },
    };

    const success = editingMilestone
      ? await update(milestoneData)
      : await create(milestoneData);

    if (success) {
      setDialogVisible(false);
      setEditingMilestone(null);
    }
  };

  const handleDelete = async (milestone: Milestone) => {
    confirmDialog({
      message: `Are you sure you want to delete milestone at level ${milestone.levelNumber}?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        const success = await deleteMilestone(milestone.id);
        if (success) {
          setDialogVisible(false);
          setEditingMilestone(null);
        }
      },
    });
  };

  const handleDragStart = (e: React.DragEvent, levelNumber: number) => {
    const milestone = milestonesMap.get(levelNumber);
    if (milestone) {
      e.dataTransfer.setData('text/plain', JSON.stringify(milestone));
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetLevel: number) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;

    try {
      const sourceMilestone: Milestone = JSON.parse(data);
      const targetMilestone = milestonesMap.get(targetLevel);

      if (targetMilestone && targetMilestone.type === sourceMilestone.type) {
        return;
      }

      if (sourceMilestone.levelNumber === targetLevel) {
        return;
      }

      await move(sourceMilestone.levelNumber, targetLevel);
    } catch (error) {
      console.error('Failed to move milestone:', error);
    }
  };

  const getCardColor = (milestone: Milestone | undefined) => {
    if (!milestone) return 'var(--surface-ground)';
    return milestone.type === MilestoneType.Primary
      ? 'rgba(173, 216, 230, 0.3)'
      : 'rgba(255, 182, 193, 0.3)';
  };

  const getCardBorderColor = (milestone: Milestone | undefined) => {
    if (!milestone) return 'var(--surface-border)';
    return milestone.type === MilestoneType.Primary
      ? 'rgba(173, 216, 230, 0.8)'
      : 'rgba(255, 182, 193, 0.8)';
  };

  const milestoneTypeOptions = [
    { label: 'Primary', value: MilestoneType.Primary },
    { label: 'Secondary', value: MilestoneType.Secondary },
  ];

  return (
    <div className="page-milestones p-3">
      <ConfirmDialog />
      <Toolbar
        start={
          <div className="flex align-items-center gap-2">
            <label htmlFor="levelFilter" className="font-semibold">
              Filter by Level:
            </label>
            <InputNumber
              id="levelFilter"
              value={levelFilter}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setLevelFilter(e.value && e.value > 0 ? e.value : null)
              }
              min={1}
              max={maxLevel}
              placeholder="All levels"
              showButtons
              className="w-12rem"
            />
          </div>
        }
      />

      <div className="milestones-grid mt-3">
        {filteredLevels.map((levelNumber) => {
          const milestone = milestonesMap.get(levelNumber);
          return (
            <div
              key={levelNumber}
              className="milestone-card"
              style={{
                backgroundColor: getCardColor(milestone),
                borderColor: getCardBorderColor(milestone),
              }}
              draggable={!!milestone}
              onDragStart={(e) => handleDragStart(e, levelNumber)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, levelNumber)}
              onClick={() => handleCardClick(levelNumber)}
            >
              <div className="milestone-card-content">
                <div className="milestone-level-number">{levelNumber}</div>
                {milestone ? (
                  <div className="milestone-info">
                    <div className="milestone-type">
                      {milestone.type === MilestoneType.Primary
                        ? 'Primary'
                        : 'Secondary'}
                    </div>
                    <div className="milestone-reward">
                      {RewardTypeLabels[milestone.metadata.reward.type]} x
                      {milestone.metadata.reward.amount}
                    </div>
                  </div>
                ) : (
                  <div className="milestone-add">
                    <i className="pi pi-plus"></i>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog
        header={editingMilestone ? 'Edit Milestone' : 'Create Milestone'}
        visible={dialogVisible}
        style={{ width: '50vw' }}
        onHide={() => {
          setDialogVisible(false);
          setEditingMilestone(null);
        }}
        footer={
          <div>
            {editingMilestone && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                onClick={() => handleDelete(editingMilestone)}
                severity="danger"
                className="mr-2"
              />
            )}
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setDialogVisible(false);
                setEditingMilestone(null);
              }}
              text
            />
            <Button label="Save" icon="pi pi-check" onClick={handleSave} />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label htmlFor="levelNumber" className="block mb-2">
              Level Number *
            </label>
            <InputNumber
              id="levelNumber"
              value={formData.levelNumber || 1}
              onValueChange={(e: InputNumberValueChangeEvent) =>
                setFormData({ ...formData, levelNumber: e.value || 1 })
              }
              min={1}
              max={maxLevel}
              className="w-full"
            />
          </div>

          <div className="field">
            <label htmlFor="milestoneType" className="block mb-2">
              Milestone Type *
            </label>
            <Dropdown
              id="milestoneType"
              value={formData.type}
              options={milestoneTypeOptions}
              onChange={(e) => setFormData({ ...formData, type: e.value })}
              className="w-full"
            />
          </div>

          <div className="field">
            <label className="block mb-2">Reward *</label>
            <RewardEditor
              reward={formData.metadata?.reward || {
                type: RewardType.Credits,
                amount: 0,
                innerRewards: [],
              }}
              onChange={(reward) =>
                setFormData({
                  ...formData,
                  metadata: { ...formData.metadata, reward },
                })
              }
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default MilestonesPage;
